// @flow

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'gatsby'
import useIsMobile from '../hooks/useIsMobile'
import Container from './Container'

type Logo = {
  file: string,
}

type LanguageItem = {
  title: string,
  code: string,
  active: boolean,
}

type Navigation = {
  title: string,
  url: string,
}

type Props = {
  items: Array<Navigation>,
  language: Array<LanguageItem>,
  currentLanguage: string,
  title: string,
  logo: Logo,
}

const Header = ({ items, language, currentLanguage, logo }: Props) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isMobile = useIsMobile()


  const handleMenuClick = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const menuVariants = {
    hidden: { height: 0 },
    show: { height: "auto" }
  }

  const logoLink = currentLanguage === "ca" ? "/" : `/${currentLanguage}` 

  const handleHeader = () => {
    if (isMobile) {
      return (
        <div className="fixed top-0 left-0 right-0 z-40 flex flex-col items-stretch bg-white shadow-xs">
          <div className="flex items-center justify-between h-16 pl-5 pr-1">
            <Link to={logoLink}>
              <img src={logo.file} className="w-auto h-8 cursor-pointer" />
            </Link>
            <button
              type="button"
              className="flex flex-col items-center justify-center w-12 h-12 focus:outline-none"
              onClick={() => handleMenuClick()}
            >
              <div className="w-5 p-px bg-dark-gray" />
              <div className="w-5 p-px my-1 bg-dark-gray" />
              <div className="w-5 p-px bg-dark-gray" />
            </button>
          </div>
          <motion.div
            className="overflow-y-hidden bg-gray"
            transition={{ duration: 0.25, type: "tween", ease: "circOut" }}
            animate={mobileMenuOpen ? "show" : "hidden"}
            variants={menuVariants}
            initial="hidden"
          >
            <nav>
              <ul className="flex flex-col items-stretch justify-start">
                {items.map(item => (
                  <li key={item.title} className="py-3 border-b-2 border-white">
                    <Link
                      to={item.url} 
                      className="mx-3 text-dark-gray"
                      activeClassName="text-black-important"
                    >
                      <span className="px-3 font-bold">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div key="language">
              <ul className="flex items-center justify-start px-4 py-3">
                {language.map(item => (
                  <li key={item.code}>
                    <Link
                      to={`/${item.code}`} 
                      className={`mx-1 ${item.active ? "text-black" : "text-dark-gray"}`}
                    >
                      <span className="px-1 font-bold">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      )
    }
  
    // desktop version
    if (!isMobile) {
      return (
        <div className="fixed top-0 left-0 right-0 z-40 hidden h-20 py-5 bg-white shadow-xs lg:block">
          <Container>
            <div className="flex items-center justify-between">
              <div className="mr-3">
                <Link to={logoLink}>
                  <img src={logo.file} className="w-auto h-10 cursor-pointer" />
                </Link>
              </div>
              <div key="navigation-and-language" className="flex items-center justify-end">
                <nav key="navigation" className="">
                  <ul className="flex items-center justify-end">
                    {items.map(item => (
                      <li key={item.title}>
                        <Link
                          to={item.url} 
                          className="mx-2 text-dark-gray"
                          activeClassName="text-black-important"
                        >
                          <span className="font-bold">{item.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div key="language">
                  <ul className="flex items-center justify-end ml-4">
                    {language.map(item => (
                      <li key={item.code}>
                        <Link
                          to={`/${item.code}`} 
                          className={`mx-1 ${item.active ? "text-black" : "text-dark-gray"}`}
                        >
                          <span className="px-1 font-bold">{item.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Container>
        </div>
      )
    }
  }

  return (
    <>
      <div key="header-spacer" className="h-16 lg:h-20" />
      {handleHeader()}
    </>
  )
}

export default Header
